import { useCallback } from 'react'
import styled, { css } from 'styled-components'
import PropTypes from 'prop-types'

import { vagRundFont } from '@h2/components/mixins'
import {
  hexToHsl,
  WHITE,
  BLACK,
  PG20S,
  PG90T,
  PG10S,
  SLB,
  SLB90T,
  SLB10S,
  SLR,
  SLR90T,
  SLR10S,
  SO,
  SO90T,
  SO10S,
  SY,
  SY90T,
  SY10S,
  PB90T,
  PB70T,
  PB20T
} from '@h2/components/colors'

import { useNavigator } from '../hooks/useNavigator'

const fontSizes = {
  small: '12px',
  medium: '14px',
  default: '16px',
  large: '20px'
}
const weights = {
  normal: 400,
  bold: 700
}
const paddings = {
  small: '5px 8px',
  medium: '6px 10px',
  default: '8px 16px',
  large: '10px 18px'
}
const colors = {
  button: 'white',
  'outlined-cta': PG10S,
  hover: {
    button: 'white',
    'outlined-cta': 'white'
  },
  default: PG10S,
  blue: SLB,
  red: SLR,
  orange: SO,
  promo: SY,
  monochrome: BLACK
}
const hoverBgColors = {
  default: PG20S,
  monochrome: PB20T,
  promo: SY10S,
  blue: SLB10S,
  orange: SO10S,
  red: SLR10S,
  variant: {
    text: {
      default: PG90T,
      monochrome: PB90T,
      promo: SY90T,
      blue: SLB90T,
      orange: SO90T,
      red: SLR90T
    },
    outlined: colors,
    'outlined-cta': colors
  }
}
const borders = {
  button: 'transparent',
  'outlined-cta': PG10S
}
const iconSizes = {
  small: 'tiny',
  medium: 'small',
  default: 'small',
  large: 'small'
}
const fontColors = {
  default: WHITE,
  blue: WHITE,
  orange: WHITE,
  red: WHITE,
  monochrome: WHITE,
  promo: BLACK,
  variant: {
    link: colors,
    text: colors,
    outlined: {
      ...colors,
      promo: BLACK
    },
    'outlined-cta': {
      ...colors,
      promo: BLACK
    }
  }
}
const getFontColor = ({ display, variant, color, icon, disabled }) => {
  if (disabled) {
    return PB70T
  }

  if (color) {
    return color
  }

  if (icon && /outline/.test(variant)) {
    return BLACK
  }

  return fontColors.variant[variant]?.[display] ?? fontColors[display]
}
const getHoverFontColor = ({ display, variant, color, icon, disabled }) => {
  if (disabled) {
    return PB70T
  }

  if (color) {
    return color
  }

  if (icon && /outline/.test(variant)) {
    return BLACK
  }

  return fontColors[display]
}
const getBackgroundColor = ({ display, variant, fill, disabled }) => {
  if (disabled) {
    return /outline/.test(variant) ? WHITE : PB90T
  }

  if (fill) {
    return fill
  }

  if (/outline/.test(variant)) {
    return WHITE
  }

  return colors[display]
}
const getHoverBackgroundColor = ({ display, variant, disabled, icon, fill }) => {
  if (disabled) {
    return /outline/.test(variant) ? 'transparent' : PB90T
  }

  if (fill) {
    const [hue, sat, light] = hexToHsl(fill)

    // Add 10% shade by reducing the light value
    return `hsl(${hue}, ${sat}%, ${light - light / 10}%)`
  }

  if (icon && /outline/.test(variant)) {
    return '#f7f7f7'
  }

  return hoverBgColors.variant[variant]?.[display] ?? hoverBgColors[display]
}
const getBorderColor = ({ display, variant, icon, disabled }) => {
  if (/outline/.test(variant)) {
    if (disabled) {
      return PB90T
    }

    if (icon) {
      return PB90T
    }

    return colors[display]
  }

  return 'transparent'
}
const button = css`
  display: inline-block;
  box-sizing: border-box;
  background-color: ${getBackgroundColor};
  border-radius: 4px;
  border-width: 1px;
  border-style: solid;
  border-color: ${getBorderColor};
  font-weight: ${({ weight }) => weights[weight]};
  font-size: ${({ size, fontSize }) => (fontSize ? fontSize : fontSizes[size])};
  ${({ variant }) => (/cta/.test(variant) ? vagRundFont : 'font-weight: 700')};
  padding: ${({ size }) => paddings[size]};

  &:link,
  &:visited {
    color: ${getFontColor};
    text-decoration: none;
  }

  &:hover {
    background-color: ${getHoverBackgroundColor};
    text-decoration: none;
    color: ${getHoverFontColor};
    border-color: ${({ variant }) => borders[variant]};
  }
`
const hover = css`
  &:hover {
    text-decoration: underline;
  }
`
const Anchor = styled.a`
  font-size: ${({ fontSize }) => fontSize ?? '16px'};
  font-weight: ${({ weight }) => weights[weight]};
  color: ${({ color }) => color ?? BLACK};
  text-decoration: ${({ textDecoration }) => textDecoration ?? 'none'};
  text-align: ${({ align }) => align};
  display: ${({ isFullWidth }) => (isFullWidth ? 'inline-block' : 'inline')};
  width: ${({ isFullWidth }) => (isFullWidth ? '100%' : 'auto')};
  background-color: transparent;
  line-height: 1;

  > span[role='img'] {
    cursor: pointer;
  }

  &:focus {
    color: black;
  }

  ${({ isHoverStyled }) => (isHoverStyled ? hover : null)};
  ${({ variant }) => (!/anchor/.test(variant) ? button : null)};
`
const getImageMargin = ({ iconAtTop, align }) => {
  if (iconAtTop) {
    return 0
  }

  if (/right/.test(align)) {
    return '0 8px 0 0'
  }

  return '0 0 0 8px'
}
const getFlexDirection = ({ iconAtTop, align }) => {
  if (iconAtTop) {
    return 'column'
  }

  if (/right/.test(align)) {
    return 'row-reverse'
  }

  return 'row'
}
const Wrap = styled.div`
  display: flex;
  align-items: center;
  flex-direction: ${getFlexDirection};
`
const TextWrap = styled.span`
  order: ${({ iconAtTop }) => (iconAtTop ? 2 : 1)};
`
const IconWrap = styled.span`
  display: inline-flex;
  margin: ${getImageMargin};
  order: ${({ iconAtTop }) => (iconAtTop ? 1 : 2)};

  > span[role='img'] {
    cursor: pointer;
  }
`
const renderBody = ({ size, align, isFullWidth, children, iconAtTop, icon: Icon }) => {
  return (
    <Wrap align={align} iconAtTop={iconAtTop}>
      <TextWrap isFullWidth={isFullWidth} iconAtTop={iconAtTop}>
        {children}
      </TextWrap>
      {Icon ? (
        <IconWrap align={align} iconAtTop={iconAtTop}>
          <Icon size={iconSizes[size]} />
        </IconWrap>
      ) : null}
    </Wrap>
  )
}
const hasClickModifierKey = event =>
  Boolean(event.ctrlKey || event.shiftKey || event.metaKey || event.altKey)
const Link = ({
  children,
  href,
  fontSize,
  onClick,
  weight = 'normal',
  ssr = false,
  size = 'default',
  align = 'center',
  isHoverStyled = true,
  isFullWidth = false,
  icon = null,
  iconAtTop = false,
  variant = 'anchor',
  display = 'default',
  isDisabled = false,

  ...rest
}) => {
  const navigator = useNavigator()
  const handleOnClick = useCallback(
    evt => {
      if (typeof onClick === 'function') {
        return onClick(evt)
      }

      /**
       * Ignore everything but,
       * !ssr: Links that do not opt-out of frontend routing
       * !hasClickModifierKey(evt): Clicks without modifier keys
       * evt.button === 0: Left clicks on the mouse
       */
      if (!ssr && !hasClickModifierKey(evt) && evt.button === 0) {
        evt.preventDefault()
        navigator(href)
      }
    },
    [navigator, href, ssr, onClick]
  )

  return (
    <Anchor
      href={href}
      weight={weight}
      fontSize={fontSize}
      size={size}
      align={align}
      isFullWidth={isFullWidth}
      isHoverStyled={isHoverStyled}
      onClick={handleOnClick}
      icon={icon}
      iconAtTop={iconAtTop}
      variant={variant}
      display={display}
      disabled={isDisabled}
      {...rest}>
      {icon
        ? renderBody({
            size,
            align,
            isFullWidth,
            children,
            iconAtTop,
            icon
          })
        : children}
    </Anchor>
  )
}

Link.propTypes = {
  /** The relative size of the link when displayed as a button */
  size: PropTypes.oneOf(['small', 'medium', 'default', 'large']),
  /** Whether the link should look like an anchor or a button */
  variant: PropTypes.oneOf(['anchor', 'button', 'outlined', 'outlined-cta']),
  /** Sets the predefined styling applied to the link. */
  display: PropTypes.oneOf(['default', 'promo', 'monochrome', 'blue', 'orange', 'red']),
  /** The link text font size */
  fontSize: PropTypes.string,
  /** The link text font weight */
  weight: PropTypes.oneOf(['normal', 'bold']),
  /** The text alignment of the link */
  align: PropTypes.oneOf(['left', 'center', 'right']),
  /** Whether the button is disalbed or not. */
  isDisabled: PropTypes.bool,
  /** Whether the link spans the full width of the parent */
  isFullWidth: PropTypes.bool,
  /** Whether the hover state of the link is styled */
  isHoverStyled: PropTypes.bool,
  /** Callback when link is clicked */
  onClick: PropTypes.func,
  /** @ignore The link children */
  children: PropTypes.node.isRequired,
  /** The URL to link to */
  href: PropTypes.string,
  /** Whether the path defined in href must be server-side rendered */
  ssr: PropTypes.bool,
  /** Whether the icon is atop the text */
  iconAtTop: PropTypes.bool,
  /** Displays an icon inside the link. */
  icon: PropTypes.elementType,
  /** Sets the background color of the button. Overrides `display` prop. */
  fill: PropTypes.string,
  /** Sets the text color of the button. Overrides the `display` prop. */
  color: PropTypes.string
}

export { Link }
